import type { EventAiTagVariant } from './event-detail.model';

/** UI popover payload — only `event.id` / `eventId` is sent to Dori today. */
export interface AiFeedbackRequest {
  readonly feedback: {
    readonly variant: EventAiTagVariant;
    readonly falsePositive: true;
  };
  readonly detection: {
    readonly id: string;
    readonly eventType: EventAiTagVariant;
    /** Optional — backend-rendered overlays; omit when geometry is server-owned. */
    readonly box?: {
      readonly x: number;
      readonly y: number;
      readonly width: number;
      readonly height: number;
      readonly offsetSeconds: number;
    };
  };
  readonly camera: {
    readonly cameraId: string;
    readonly numericId: number;
    readonly name: string;
  };
  readonly event: {
    readonly eventId: string;
  };
}

/** POST /dori/flag_inference — Dori Inference Callback API */
export interface DoriFlagInferenceRequest {
  readonly event_id: string;
  readonly annotation: {
    readonly reason: string;
  };
}

export interface DoriFlagInferenceResponse {
  readonly success?: true;
  readonly status?: 'accepted';
  readonly flag_id: string;
  readonly event_id: string;
  readonly received_at: string;
}

export interface AiFeedbackResponse {
  readonly success: boolean;
  readonly message?: string;
  readonly flagId?: string;
}

/** Fallback annotation reason when variant is missing/unknown. */
export const DORI_FALSE_POSITIVE_REASON = 'Incorrect object classification';

const DORI_REASON_BY_VARIANT: Record<EventAiTagVariant, string> = {
  motion: 'False Motion Detection',
  human: 'False Human Detection',
  vehicle: 'False Vehicle Detection',
  gun: 'False Gun Detection',
};

/** Maps popover detection variant → Dori `annotation.reason`. */
export function doriAnnotationReasonFromVariant(
  variant: EventAiTagVariant | null | undefined,
): string {
  if (!variant) {
    return DORI_FALSE_POSITIVE_REASON;
  }
  return DORI_REASON_BY_VARIANT[variant] ?? DORI_FALSE_POSITIVE_REASON;
}

export class DoriAuthConfigError extends Error {
  constructor(message = 'Dori API token is missing — set environment.doriApiToken locally') {
    super(message);
    this.name = 'DoriAuthConfigError';
  }
}
